import { Link } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";

import { NotificationModel } from "@interfaces/index";
import {
  UploadSvg,
  FlagCheckSvg,
  KeySvg,
  ChecksSvg,
  MessageCircleSvg,
} from "@components/index";

export const NotificationItem = ({
  notification: {
    uniqueId,
    type,
    title = "",
    message = "",
    actionLabel = "",
    updatedAt,
  },
  hideAssetItem = false,
  readOne,
  onClose,
}: {
  notification: NotificationModel;
  hideAssetItem?: boolean;
  readOne?: () => void;
  onClose: () => void;
}) => {
  let kind;
  if (
    ["App\\Notifications\\AssetAttachmentReminderNotification"].includes(type)
  ) {
    kind = "asset";
  } else if (
    [
      "App\\Notifications\\SubmissionAccountantAssignedNotification",
      "App\\Notifications\\SubmissionArchivedNotification",
      "App\\Notifications\\SubmissionCompletedNotification",
      "App\\Notifications\\SubmissionDueSoonNotification",
      "App\\Notifications\\SubmissionInReviewNotification",
      "App\\Notifications\\SubmissionOverdueNotification",
      "App\\Notifications\\SubmissionReopenedDuePeriodNotification",
      "App\\Notifications\\SubmissionReopenedNotification",
      "App\\Notifications\\SubmissionSubmittedNotification",
      "App\\Notifications\\SubmissionReopenedWithNoteNotification",
    ].includes(type)
  ) {
    kind = "submission";
  } else if (
    [
      "App\\Notifications\\CallRequestedNotification",
      "App\\Notifications\\CustomResetPassword",
      "App\\Notifications\\CustomVerifyEmail",
      "App\\Notifications\\RegistrationRequestedNotification",
      "App\\Notifications\\SendInviteLink",
      "App\\Notifications\\UserActivationNotification",
      "App\\Notifications\\UserDeactivationNotification",
    ].includes(type)
  ) {
    kind = "account";
  } else if (
    [
      "App\\Notifications\\QuestionSetAssignedNotification",
      "App\\Notifications\\QuestionSetReminderNotification",
      "App\\Notifications\\QuestionSetLateEnrollmentNotification",
    ].includes(type)
  ) {
    kind = "questionnaire";
  } else if (
    [
      "App\\Notifications\\QueryRaisedByFbtcNotification",
      "App\\Notifications\\QueryRepliedByFbtcNotification",
      "App\\Notifications\\QueryResolvedNotification",
    ].includes(type)
  ) {
    kind = "querySystem";
  }

  let timeLabel = formatDistanceToNowStrict(new Date(updatedAt), {
    addSuffix: true,
  });
  timeLabel = timeLabel
    .replace(" minutes", " min")
    .replace(" minute", " min")
    .replace(" hours", " h")
    .replace(" hour", " h")
    .replace(" seconds", " s");

  let href;
  switch (kind) {
    case "asset":
      href = `/assets/${uniqueId}`;
      break;
    case "submission":
      href = `/submissions/details/${uniqueId}`;
      break;
    case "questionnaire":
      href = `/submissions/details/${uniqueId}?activeTab=Questionnaire`;
      break;
    case "querySystem":
      href = `/queries?queryId=${uniqueId}`;
      break;
  }

  let linkLabel;
  switch (kind) {
    case "asset":
      linkLabel = "View asset";
      break;
    case "submission":
      linkLabel = "View submission";
      break;
    case "questionnaire":
      linkLabel = "View questionnaire";
      break;
    case "querySystem":
      linkLabel = "View query";
      break;
  }

  if (hideAssetItem && kind === "asset") {
    return null;
  }

  // Will be included in the future when the questionnaire is ready to be used
  if (kind === "questionnaire") {
    return null;
  }

  return (
    <div className="group flex justify-between gap-x-4">
      <div className="flex gap-x-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-108 text-black-54 *:h-5 *:w-5">
          {kind === "asset" && <UploadSvg />}
          {kind === "submission" && <FlagCheckSvg />}
          {kind === "account" && <KeySvg />}
          {kind === "questionnaire" && <FlagCheckSvg />}
          {kind === "querySystem" && <MessageCircleSvg />}
        </div>
        <div className="flex flex-col items-start gap-y-1">
          <div className="font-bold text-black-100">
            {kind === "asset" && (title || "Upload documents for Asset")}
            {kind === "submission" && (title || "Submission notification")}
            {kind === "account" && (title || "Account notification")}
            {kind === "questionnaire" &&
              (title || "Questionnaire notification")}
            {kind === "querySystem" && (title || "Query notification")}
          </div>
          <div className="text-black-54">{message}</div>
          {href && uniqueId && (actionLabel || linkLabel) && (
            <div>
              <Link
                to={href}
                className="text-primary-300 underline hover:no-underline"
                onClick={onClose}
              >
                {actionLabel || linkLabel}
              </Link>
            </div>
          )}
        </div>
      </div>
      <div className="flex w-16 shrink-0 flex-col items-end justify-between gap-y-1">
        {!readOne ? (
          <div className="text-right text-100 font-bold text-black-54">
            {timeLabel}
          </div>
        ) : (
          <>
            <div className="text-right text-100 font-bold text-black-54 group-hover:hidden">
              {timeLabel}
            </div>
            <button
              role="button"
              className="hidden text-black-32 hover:text-primary-300 group-hover:inline"
              onClick={readOne}
            >
              <ChecksSvg />
            </button>
          </>
        )}
      </div>
    </div>
  );
};
